import { Logger } from '../logger';

/**
 * Customizes typography of the cast-media-player overlay (tv-overlay).
 *
 * The tv-overlay is a Chromecast platform component with unknown internal
 * structure. Instead of guessing CSS class names, we:
 *   1. Walk the DOM tree inside cast-media-player's shadowRoot
 *   2. Dump the full structure to the debug logger (one-time)
 *   3. Apply inline styles to elements matching player data values
 *   4. Re-apply on every PlayerData change (title, subtitle, time update)
 */

const TITLE_STYLES: Partial<CSSStyleDeclaration> = {
  fontFamily: 'CircularXX, sans-serif',
  fontWeight: '400',
  fontSize: '48px',
  lineHeight: '120%',
  letterSpacing: '-0.02em',
};

const SUBTITLE_STYLES: Partial<CSSStyleDeclaration> = {
  fontFamily: 'CircularXX, sans-serif',
  fontWeight: '400',
  fontSize: '36px',
  lineHeight: '120%',
  letterSpacing: '-0.02em',
};

const TIME_STYLES: Partial<CSSStyleDeclaration> = {
  fontFamily: 'CircularXX, sans-serif',
  fontWeight: '700',
  fontSize: '24px',
  lineHeight: '28px',
  letterSpacing: '-0.02em',
};

function applyStyles(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  for (const [prop, value] of Object.entries(styles)) {
    el.style.setProperty(
      prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`),
      value as string,
      'important',
    );
  }
}

interface CollectedElements {
  textEls: HTMLElement[];
  imgEls: HTMLImageElement[];
}

/** Collect text-bearing leaf elements and <img> elements (walks into open shadow DOMs). */
function collectElements(root: Element | ShadowRoot): CollectedElements {
  const textEls: HTMLElement[] = [];
  const imgEls: HTMLImageElement[] = [];

  function walk(node: Element | ShadowRoot): void {
    if (node instanceof HTMLElement && node.shadowRoot) {
      walk(node.shadowRoot);
    }
    const children = node instanceof ShadowRoot ? node.children : node.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      // Recurse into shadow DOM of children
      if (child.shadowRoot) {
        walk(child.shadowRoot);
      }
      // Collect <img> elements
      if (child.tagName === 'IMG') {
        imgEls.push(child as HTMLImageElement);
      }
      // Leaf text node
      if (child.children.length === 0 && (child.textContent ?? '').trim().length > 0) {
        textEls.push(child);
      }
      // Continue walking
      if (child.children.length > 0) {
        walk(child);
      }
    }
  }

  walk(root);
  return { textEls, imgEls };
}

/** Time pattern: matches "00:00", "1:23:45", etc. */
const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

let hasDumped = false;

function dumpOverlayTree(root: Element | ShadowRoot, label: string): void {
  const lines: string[] = [];
  function walk(node: Element | ShadowRoot, depth: number): void {
    const children = node instanceof ShadowRoot ? node.children : node.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i] as HTMLElement;
      const indent = '  '.repeat(depth);
      const tag = el.tagName.toLowerCase();
      const cls = el.className ? `.${String(el.className).replace(/\s+/g, '.')}` : '';
      const id = el.id ? `#${el.id}` : '';
      const txt = el.children.length === 0 ? ` "${(el.textContent ?? '').slice(0, 40)}"` : '';
      lines.push(`${indent}<${tag}${id}${cls}>${txt}`);
      if (el.shadowRoot) {
        lines.push(`${indent}  #shadow-root`);
        walk(el.shadowRoot, depth + 2);
      }
      if (el.children.length > 0) {
        walk(el, depth + 1);
      }
    }
  }
  walk(root, 0);
  Logger.info('OVERLAY', `--- ${label} (${lines.length} elements) ---`);
  for (const line of lines) {
    Logger.info('OVERLAY', line);
  }
}

/** Cloudinary domain used to identify our thumbnail images. */
const BP_IMAGE_DOMAIN = 'images.brasilparalelo.com.br';

function styleOverlayElements(playerShadow: ShadowRoot): void {
  // Get current player data for matching
  const context = cast.framework.CastReceiverContext.getInstance();
  const pm = context.getPlayerManager();
  const mediaInfo = pm.getMediaInformation();
  const metadata = mediaInfo?.metadata;

  const meta = metadata as Record<string, unknown> | undefined;
  const titleText = (String(meta?.title ?? '')).trim();
  const subtitleText = (String(meta?.subtitle ?? '')).trim();

  // Get image dimensions from metadata (set by load-interceptor)
  const images = meta?.images as Array<{ url?: string; width?: number; height?: number }> | undefined;
  const metaImage = images?.[0];
  const isDvd = metaImage?.width === 96;

  // Collect all elements from the shadow DOM tree
  const { textEls, imgEls } = collectElements(playerShadow);

  // One-time dump of the full tree
  if (!hasDumped && (textEls.length > 0 || imgEls.length > 0)) {
    hasDumped = true;
    dumpOverlayTree(playerShadow, 'cast-media-player shadow DOM');
  }

  let styledTitle = false;
  let styledSubtitle = false;
  let styledTime = 0;
  let styledImage = false;

  // Style text elements
  for (const el of textEls) {
    const text = (el.textContent ?? '').trim();
    if (!text) continue;

    if (titleText && text === titleText && !styledTitle) {
      applyStyles(el, TITLE_STYLES);
      styledTitle = true;
    } else if (subtitleText && text === subtitleText && !styledSubtitle) {
      applyStyles(el, SUBTITLE_STYLES);
      styledSubtitle = true;
    } else if (TIME_RE.test(text)) {
      applyStyles(el, TIME_STYLES);
      styledTime++;
    }
  }

  // Style thumbnail image
  for (const img of imgEls) {
    if (img.src?.includes(BP_IMAGE_DOMAIN) && !styledImage) {
      const w = isDvd ? 96 : 260;
      applyStyles(img, {
        width: `${w}px`,
        height: '146px',
        objectFit: 'cover',
        borderRadius: '4px',
      });
      styledImage = true;
    }
  }

  if (styledTitle || styledSubtitle || styledTime > 0 || styledImage) {
    Logger.info('OVERLAY', `Styled: title=${styledTitle} subtitle=${styledSubtitle} time=${styledTime} image=${styledImage}${isDvd ? ' (dvd)' : ' (thumb)'}`);
  }
}

export function injectPlayerOverlayStyles(): void {
  const player = document.querySelector('cast-media-player');
  if (!player) {
    Logger.warn('OVERLAY', 'cast-media-player not found');
    return;
  }

  const shadow = player.shadowRoot;
  if (!shadow) {
    Logger.warn('OVERLAY', 'cast-media-player shadowRoot not accessible');
    return;
  }

  Logger.info('OVERLAY', 'shadowRoot accessible — observing for overlay changes');

  // Use MutationObserver to re-apply styles whenever the overlay DOM changes
  // (tv-overlay updates when media state changes)
  let rafId = 0;
  const observer = new MutationObserver(() => {
    // Debounce with rAF to avoid excessive re-styling
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => styleOverlayElements(shadow));
  });

  observer.observe(shadow, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Also apply immediately in case overlay is already rendered
  styleOverlayElements(shadow);
}
